<script setup lang="ts" generic="T">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import SelectionLabel from '@/components/forms/controls/SelectionLabel.vue';
import type { LocaleSchema } from '@/locales/index.ts';

type Props<T> = {
  mode: 'header' | 'cell';
  selectionMode?: 'single' | 'multiple';
  isSelected?: boolean;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  item?: T;
}

const { t } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const props = withDefaults(defineProps<Props<T>>(), {
  selectionMode: 'single',
  isSelected: false,
  isAllSelected: false,
  isIndeterminate: false,
});

const emit = defineEmits<{
  'selection-change': [item: T, isSelected: boolean];
  'select-all': [];
  'deselect-all': [];
}>();

const headerAriaLabel = computed(() => {
  if (props.mode === 'header') {
    return props.isAllSelected 
      ? t('dataTable.selection.deselectAll') 
      : t('dataTable.selection.selectAll');
  }
  return undefined;
});

const cellAriaLabel = computed(() => {
  if (props.mode === 'cell') {
    return props.isSelected 
      ? t('dataTable.selection.deselectItem') 
      : t('dataTable.selection.selectItem');
  }
  return undefined;
});

const headerScreenReaderText = computed(() => {
  if (props.mode === 'header') {
    return props.isAllSelected 
      ? t('dataTable.selection.deselectAll') 
      : t('dataTable.selection.selectAll');
  }
  return undefined;
});

const cellScreenReaderText = computed(() => {
  if (props.mode === 'cell') {
    return props.isSelected 
      ? t('dataTable.selection.selected') 
      : t('dataTable.selection.notSelected');
  }
  return undefined;
});

function handleSelectionChange(checked: boolean) {
  if (props.mode === 'cell' && props.item) {
    emit('selection-change', props.item, checked);
  } else if (props.mode === 'header' && props.selectionMode === 'multiple') {
    if (checked) {
      emit('select-all');
    } else {
      emit('deselect-all');
    }
  }
}
</script>

<template>
  <template v-if="mode === 'header'">
    <th class="dataTable-header dataTable-selectionHeader">
      <SelectionLabel v-if="selectionMode === 'multiple'"
                      type="checkbox"
                      :checked="isAllSelected"
                      :indeterminate="isIndeterminate"
                      :aria-label="headerAriaLabel"
                      :screen-reader-text="headerScreenReaderText"
                      @change="handleSelectionChange" />
    </th>
  </template>
  
  <template v-else>
    <td class="dataTable-cell dataTable-selectionCell">
      <div class="dataTable-selectionCellContent">
        <slot>
          <SelectionLabel :type="selectionMode === 'single' ? 'radio' : 'checkbox'"
                          :checked="isSelected"
                          :aria-label="cellAriaLabel"
                          :screen-reader-text="cellScreenReaderText"
                          @change="handleSelectionChange" />
        </slot>
      </div>
    </td>
  </template>
</template>

<style lang="scss">
.dataTable-selectionHeader {
  width: 60px;
  text-align: center;
  padding: 0.75rem 0.5rem;
}

.dataTable-selectionCell {
  width: 60px;
  text-align: center;
  padding: 0.75rem 0.5rem;
}

.dataTable-selectionCellContent {
  display: flex;
  justify-content: end;
  align-items: center;
}
</style>
